import { PageType } from '@app-types/PageType'
import MarkedButton from '@components/Home/MarkedButton'
import { getDuaPageAndPartKey } from '@components/DuaPage/duaPartHelper'
import Breadcrumb from '@components/shared/Breadcrumb/Breadcrumb'
import FavoriteButton from '@components/shared/FavoriteButton'
import { contentLibrary } from '@components/shared/consts'
import { useUserData } from '@contexts/UserDataContext'
import { getNativeReaderHref } from 'helpers/nativeReaderHref'
import Link from 'next/link'
import { DefaultSeo } from 'next-seo'
import { FC } from 'react'
import { formatDuaGenitive, formatDuaTitle } from '../../../utils/formatDuaTitle'

export const duas = [...contentLibrary.filter((item) => item.basePath === '/dualar')]

/** Presents the dua guide with its statically selected description. */
const GenericDuaAboutPage: FC<{ type: PageType; description: string }> = ({
  type,
  description,
}) => {
  const duaData = duas.find((duaFromDuas) => duaFromDuas.type === type)
  const duaTitle = formatDuaTitle(duaData?.name)
  const { userData } = useUserData()

  const pagesToShow = new Array(duaData?.pageCount).fill(null).map((_, i) => {
    return {
      page: i + 1,
      name: `${i + 1}. Sayfa`,
    }
  })
  const [bookmarkedPage] = getDuaPageAndPartKey(userData?.[type] || null)

  const breadcrumbItems = [
    { label: 'Dualar', href: '/dualar' },
    { label: duaData?.name || '' },
  ]

  return (
    <>
      <DefaultSeo
        title={`${duaTitle} Arapça ve Türkçe Meali ile Oku | Hayırhah`}
        description={description}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: duaData?.keywords || '',
          },
        ]}
      />
      <div className="mx-auto w-full max-w-3xl pb-8">
        <Breadcrumb items={breadcrumbItems} />

        <div className="space-y-3">
          <section className="rounded-2xl border border-slate-200/90 bg-linear-to-br from-white via-slate-50 to-slate-100/70 p-4 text-left shadow-sm dark:border-slate-700/80 dark:from-slate-800 dark:via-slate-800 dark:to-slate-900/80">
            <div className="mb-3 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary-600 dark:text-primary-300">
                  Dua Rehberi
                </p>
                <h1 className="mt-1 text-[26px] font-semibold leading-tight text-slate-900 dark:text-slate-100">
                  {duaTitle}
                </h1>
              </div>
              <FavoriteButton
                url={duaData?.url || ''}
                size="lg"
                className="rounded-full border border-slate-200 bg-white/80 dark:border-slate-600 dark:bg-slate-800/80"
              />
            </div>
            <p className="text-sm leading-6 text-slate-700 dark:text-slate-300">
              {duaTitle} sayfalarını okumak için aşağıdaki butonlara tıklayabilirsiniz.
            </p>
            <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-300">
              {description}
            </p>
            <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-300">
              Duaları okurken kaldığınız sayfayı işaretleyip tekrar açtığınızda kaldığınız
              sayfadan devam edebilirsiniz.
            </p>
          </section>

          {duaData?.infoVideo && (
            <section className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-4 text-left shadow-sm dark:border-slate-700/80 dark:bg-slate-800">
              <p className="mb-3 text-sm font-semibold text-slate-800 dark:text-slate-100">
                {formatDuaGenitive(duaData?.name)} önemini ve faziletini videodan izleyin.
              </p>
              <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
                <iframe
                  className="aspect-video w-full"
                  src={duaData?.infoVideo}
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </section>
          )}

          <section className="rounded-2xl border border-slate-200/90 bg-white p-4 dark:border-slate-700/80 dark:bg-slate-800">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Kaldığın Sayfadan Devam Et
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              Son okuduğun sayfayı tek dokunuşla aç.
            </p>
            <div className="mt-3">
              <MarkedButton
                basePath="/dualar"
                type={type}
                page={userData?.[type]}
                lastReadPage={userData?.lastReadPages?.[type]}
                className="max-w-none rounded-xl py-3"
              />
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm dark:border-slate-700/80 dark:bg-slate-800">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {duaData?.name} Sayfaları
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              İstediğiniz sayfayı seçip okumaya başlayın.
            </p>

            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {pagesToShow.map((item) => {
                const [lastReadPageKey] = getDuaPageAndPartKey(
                  userData?.lastReadPages?.[type] || null,
                )
                const isCurrent = item.page === bookmarkedPage
                const isLastRead = item.page === lastReadPageKey

                return (
                  <Link
                    key={item.page}
                    href={getNativeReaderHref(`/dualar/${duaData?.type}/${item.page}`)}
                    className={`rounded-xl border px-3 py-2.5 text-left transition-all duration-200 ${
                      isCurrent || isLastRead
                        ? 'border-primary-400 bg-primary-50 shadow-sm dark:border-primary-500/70 dark:bg-primary-900/20'
                        : 'border-slate-200 bg-slate-50 hover:border-primary-300 hover:bg-white dark:border-slate-700 dark:bg-slate-900/40 dark:hover:border-primary-700 dark:hover:bg-slate-800'
                    }`}
                  >
                    <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">
                      {item.name}
                    </span>
                    <span className="mt-0.5 block text-[11px] text-slate-500 dark:text-slate-400">
                      {isLastRead ? 'Son açılan' : isCurrent ? 'İşaretli' : 'Aç'}
                    </span>
                  </Link>
                )
              })}
            </div>
          </section>
        </div>
      </div>
    </>
  )
}

export default GenericDuaAboutPage

/** Loads one description without including the catalog in client JavaScript. */
export const getStaticProps = async ({ params }: { params: { type: PageType } }) => {
  const { type } = params
  const { getDuaDescription } = await import('../../../lib/duaSeo.server')

  return {
    props: {
      type,
      description: getDuaDescription(type),
    },
  }
}

export const getStaticPaths = async () => {
  const paths = duas.map((dua) => ({
    params: {
      type: dua.type,
    },
  }))

  return {
    paths,
    fallback: false,
  }
}
